import { db } from '../config/firebase';
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  getDocs,
  onSnapshot,
  query,
  where,
  writeBatch,
  serverTimestamp,
  deleteField,
} from 'firebase/firestore';
import { chunkComitentes, subscribeByComitentes } from './comitentesQueryService';

const LICITACIONES = 'licitaciones';
const ORDENES = 'licitacionesOrdenes';
// Fee de colocación (bps), un doc por licitación con su mismo id. Va aparte porque los
// productores leen `licitaciones` completa y el fee es información interna.
const FEES = 'licitacionesFees';

// Firestore admite 500 escrituras por batch; se corta antes para dejar margen.
const BATCH_SIZE = 400;

const toNumber = (value) => {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * Trae todas las órdenes de una licitación. Se filtra por estado en cliente
 * (son pocas por licitación) para no depender de índices compuestos.
 */
const fetchOrdenesLicitacion = async (licitacionId) => {
  const snapshot = await getDocs(query(collection(db, ORDENES), where('licitacionId', '==', licitacionId)));
  return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
};

const toDocs = (snapshot) => snapshot.docs.map(d => ({ id: d.id, ...d.data() }));

/**
 * Suscribe a las licitaciones que todavía no liquidaron al día `fechaIso`
 * (fechaLiquidacion >= fecha): son las únicas que pueden estar en ventana de caja.
 * Una sola desigualdad para no requerir un índice compuesto; estado y fecha de
 * caja se filtran en memoria (ver licitacionImpactaSaldo).
 */
export const subscribeLicitacionesSinLiquidar = (fechaIso, onData, onError) => onSnapshot(
  query(collection(db, LICITACIONES), where('fechaLiquidacion', '>=', fechaIso)),
  (snapshot) => onData(toDocs(snapshot)),
  (error) => {
    console.error('licitacionesService.subscribeLicitacionesSinLiquidar failed:', { fechaIso, error });
    if (onError) onError(error);
  },
);

/**
 * Suscribe a las órdenes de un conjunto de licitaciones, fusionadas en una sola
 * lista. En modo productor (`comitentes` = su padrón) las reglas exigen acotar
 * la consulta por `nroComitente`, así que las licitaciones se filtran en memoria.
 *
 * @returns {() => void} función de desuscripción
 */
export const subscribeOrdenesDeLicitaciones = (licitacionIds, comitentes, onData, onError) => {
  const ids = new Set((licitacionIds || []).map(String));
  if (Array.isArray(comitentes)) {
    return subscribeByComitentes(
      ORDENES,
      comitentes,
      (docs) => onData(docs.filter(o => ids.has(o.licitacionId))),
      onError,
    );
  }

  const chunks = chunkComitentes([...ids]);
  if (chunks.length === 0) {
    onData([]);
    return () => {};
  }
  // Igual que subscribeByComitentes: se emite recién cuando respondieron todos los bloques.
  const partials = new Map();
  const unsubs = chunks.map((chunk, index) => onSnapshot(
    query(collection(db, ORDENES), where('licitacionId', 'in', chunk)),
    (snapshot) => {
      partials.set(index, toDocs(snapshot));
      if (partials.size === chunks.length) onData([...partials.values()].flat());
    },
    (error) => {
      console.error('licitacionesService.subscribeOrdenesDeLicitaciones failed:', error);
      if (onError) onError(error);
    },
  ));
  return () => unsubs.forEach(unsub => unsub());
};

/**
 * Aplica el mismo update a la licitación y a un conjunto de órdenes,
 * partiendo en varios batches si hace falta.
 */
const commitEnBatches = async (licitacionId, licitacionUpdate, ordenIds, ordenUpdate) => {
  const chunks = [];
  for (let i = 0; i < ordenIds.length; i += BATCH_SIZE) {
    chunks.push(ordenIds.slice(i, i + BATCH_SIZE));
  }
  if (chunks.length === 0) chunks.push([]);

  // La licitación va en el primer batch junto con sus primeras órdenes.
  for (let i = 0; i < chunks.length; i += 1) {
    const batch = writeBatch(db);
    if (i === 0) batch.update(doc(db, LICITACIONES, licitacionId), licitacionUpdate);
    chunks[i].forEach(id => batch.update(doc(db, ORDENES, id), ordenUpdate));
    // eslint-disable-next-line no-await-in-loop
    await batch.commit();
  }
};

/**
 * Documento de `licitacionesFees`:
 * - `feesPorColocador` { [colocadorId]: bps }: licitaciones con colocadores.
 * - `feeColocacionBps` (null = sin fee): licitaciones anteriores. Se conserva el valor
 *   que tenían si después se les cargan colocadores (el `set` reemplaza el documento).
 *
 * @param {{ feeColocacionBps?: number|null, feesPorColocador?: object }} fee
 */
const buildFeeDoc = ({ feeColocacionBps, feesPorColocador }, { userId, userName }) => ({
  ...(feeColocacionBps !== undefined
    ? { feeColocacionBps: Number.isFinite(feeColocacionBps) ? feeColocacionBps : null }
    : {}),
  ...(feesPorColocador ? { feesPorColocador } : {}),
  actualizadoPorId: userId || '',
  actualizadoPorNombre: userName || '',
  updatedAt: serverTimestamp(),
});

/**
 * Crea una licitación (evento) con sus especies y colocadores embebidos y el fee de cada
 * colocador, en un mismo batch (las reglas exigen las dos cosas juntas en el alta).
 * @param {object} data - { nombre, emisor, colocadores, colocador, mercado, cuitEmisora?,
 *   nroAgente, fechaLicitacion, fechaLiquidacion, fechaCaja, especies, observaciones }
 * @param {{ userId: string, userName: string }} actor
 * @param {object} feesPorColocador - { [colocadorId]: bps } de todos los colocadores
 */
export const createLicitacion = async (data, { userId, userName }, feesPorColocador) => {
  try {
    const ref = doc(collection(db, LICITACIONES));
    const batch = writeBatch(db);
    batch.set(ref, {
      nombre: data.nombre,
      emisor: data.emisor || '',
      colocadores: data.colocadores || [],
      colocador: data.colocador || '',
      mercado: data.mercado || '',
      ...(data.cuitEmisora ? { cuitEmisora: data.cuitEmisora } : {}),
      nroAgente: data.nroAgente || '',
      fechaLicitacion: data.fechaLicitacion,
      fechaLiquidacion: data.fechaLiquidacion,
      fechaCaja: data.fechaCaja,
      estado: 'Abierta',
      especies: data.especies,
      observaciones: data.observaciones || '',
      cargaReabiertaHasta: '',
      cargaReabiertaPorNombre: '',
      createdAt: serverTimestamp(),
      creadoPorId: userId,
      creadoPorNombre: userName,
      updatedAt: serverTimestamp(),
    });
    if (feesPorColocador) {
      batch.set(doc(db, FEES, ref.id), buildFeeDoc({ feesPorColocador }, { userId, userName }));
    }
    await batch.commit();
    return ref;
  } catch (error) {
    console.error('licitacionesService.createLicitacion failed:', error);
    throw error;
  }
};

/**
 * Guarda los cambios de una licitación y, si cambió, su fee de colocación en un mismo
 * batch. `fee === undefined` deja el fee como estaba; si no, es
 * `{ feesPorColocador, feeColocacionBps? }` (con colocadores) o `{ feeColocacionBps }`
 * (licitación anterior sin colocadores).
 */
export const updateLicitacionConFee = async (id, data, fee, actor) => {
  try {
    const batch = writeBatch(db);
    batch.update(doc(db, LICITACIONES, id), { ...data, updatedAt: serverTimestamp() });
    if (fee !== undefined) {
      batch.set(doc(db, FEES, id), buildFeeDoc(fee, actor));
    }
    await batch.commit();
  } catch (error) {
    console.error('licitacionesService.updateLicitacionConFee failed:', { id, error });
    throw error;
  }
};

const SIN_FEE_CARGADO = { feeBps: null, feesPorColocador: null };

/** Lee un documento de `licitacionesFees` (sin validar más que el tipo). */
export const parseFeeDoc = (data) => {
  if (!data) return SIN_FEE_CARGADO;
  const legacy = data.feeColocacionBps;
  const fees = data.feesPorColocador;
  return {
    feeBps: typeof legacy === 'number' && Number.isFinite(legacy) ? legacy : null,
    feesPorColocador: fees && typeof fees === 'object' && !Array.isArray(fees) ? fees : null,
  };
};

/**
 * Suscribe al fee de colocación de una licitación (solo personal interno: las reglas
 * se lo niegan a los productores). Emite `{ feeBps, feesPorColocador }`: `feeBps` es el
 * fee único de las licitaciones anteriores y `feesPorColocador` el de cada colocador;
 * null lo que no esté cargado.
 *
 * @returns {() => void} función de desuscripción
 */
export const subscribeLicitacionFee = (licitacionId, onData, onError) => onSnapshot(
  doc(db, FEES, licitacionId),
  (snapshot) => onData(parseFeeDoc(snapshot.exists() ? snapshot.data() : null)),
  (error) => {
    console.error('licitacionesService.subscribeLicitacionFee failed:', { licitacionId, error });
    if (onError) onError(error);
  },
);

export const updateLicitacion = (id, data) => {
  try {
    return updateDoc(doc(db, LICITACIONES, id), { ...data, updatedAt: serverTimestamp() });
  } catch (error) {
    console.error('licitacionesService.updateLicitacion failed:', { id, error });
    throw error;
  }
};

export const setLicitacionEstado = (id, estado) => updateLicitacion(id, { estado });

/**
 * Cierra la carga de órdenes y descarta cualquier reapertura vigente.
 */
export const cerrarCarga = (id) => updateLicitacion(id, {
  estado: 'Cerrada',
  cargaReabiertaHasta: '',
  cargaReabiertaPorNombre: '',
});

/**
 * Reabre la carga. Si la fecha de licitación ya pasó, el bloqueo automático
 * volvería a activarse igual: `cargaReabiertaHasta` lo suspende hasta el final
 * de ese día (se pasa el día de hoy en ISO).
 */
export const reabrirCarga = (id, hastaIso, { userName }) => updateLicitacion(id, {
  estado: 'Abierta',
  cargaReabiertaHasta: hastaIso,
  cargaReabiertaPorNombre: userName || '',
});

/**
 * Soft delete de la licitación y todas sus órdenes activas.
 * Las órdenes borradas en cascada quedan marcadas con `eliminadoMotivo:
 * 'Cascada'` para poder restaurar solo esas y respetar las que se habían
 * eliminado a mano antes.
 *
 * @param {object} licitacion
 * @param {Array|null} ordenes - órdenes ya cargadas; si no es un array se consultan.
 */
export const softDeleteLicitacion = async (licitacion, ordenes, { userId, userName }) => {
  try {
    const auditoria = {
      estado: 'Eliminada',
      eliminadoPorId: userId,
      eliminadoPorNombre: userName,
      eliminadoAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };
    const lista = Array.isArray(ordenes) ? ordenes : await fetchOrdenesLicitacion(licitacion.id);
    const ordenIds = lista.filter(o => o.estado !== 'Eliminada').map(o => o.id);
    return await commitEnBatches(
      licitacion.id,
      auditoria,
      ordenIds,
      { ...auditoria, eliminadoMotivo: 'Cascada' }
    );
  } catch (error) {
    console.error('licitacionesService.softDeleteLicitacion failed:', { id: licitacion?.id, error });
    throw error;
  }
};

/**
 * Revierte un soft delete: la licitación vuelve como 'Cerrada' (no reabre la
 * carga por sí sola) y solo se restauran las órdenes eliminadas en cascada.
 * Las órdenes sin `eliminadoMotivo` son de antes de este campo y se tratan
 * como cascada.
 */
export const restaurarLicitacion = async (licitacion) => {
  try {
    const limpiarAuditoria = {
      eliminadoPorId: '',
      eliminadoPorNombre: '',
      eliminadoAt: deleteField(),
      updatedAt: serverTimestamp(),
    };
    const lista = await fetchOrdenesLicitacion(licitacion.id);
    const ordenIds = lista
      .filter(o => o.estado === 'Eliminada' && (o.eliminadoMotivo || 'Cascada') === 'Cascada')
      .map(o => o.id);
    return await commitEnBatches(
      licitacion.id,
      { ...limpiarAuditoria, estado: 'Cerrada' },
      ordenIds,
      { ...limpiarAuditoria, estado: 'Activa', eliminadoMotivo: '' }
    );
  } catch (error) {
    console.error('licitacionesService.restaurarLicitacion failed:', { id: licitacion?.id, error });
    throw error;
  }
};

/**
 * Crea una orden completando todos los campos requeridos por las reglas,
 * para que los updates parciales posteriores pasen la validación.
 * `colocadorId`: por cuál colocador de la licitación se colocó (obligatorio si la
 * licitación tiene colocadores; las anteriores no lo llevan).
 */
export const createOrden = (data, { userId, userName }) => {
  try {
    return addDoc(collection(db, ORDENES), {
      licitacionId: data.licitacionId,
      ...(data.colocadorId ? { colocadorId: data.colocadorId } : {}),
      especieId: data.especieId,
      nroComitente: data.nroComitente,
      cliente: data.cliente,
      cuit: data.cuit || '',
      tipoDocumento: data.tipoDocumento || '',
      comercial: data.comercial,
      tramo: data.tramo,
      tasaOfertada: toNumber(data.tasaOfertada),
      precioOfertado: toNumber(data.precioOfertado),
      cantidadVN: toNumber(data.cantidadVN),
      monedaIntegracion: data.monedaIntegracion,
      cargado: false,
      cargadoPorId: '',
      cargadoPorNombre: '',
      estadoAdjudicacion: 'Pendiente',
      vnAdjudicado: 0,
      montoAdjudicado: 0,
      cajaHecha: false,
      cajaMarcadaPorId: '',
      cajaMarcadaPorNombre: '',
      obs: data.obs || '',
      estado: 'Activa',
      eliminadoMotivo: '',
      createdAt: serverTimestamp(),
      creadoPorId: userId,
      creadoPorNombre: userName,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('licitacionesService.createOrden failed:', error);
    throw error;
  }
};

export const updateOrden = (id, data) => {
  try {
    return updateDoc(doc(db, ORDENES, id), { ...data, updatedAt: serverTimestamp() });
  } catch (error) {
    console.error('licitacionesService.updateOrden failed:', { id, error });
    throw error;
  }
};

export const softDeleteOrden = (orden, { userId, userName }) => {
  try {
    return updateDoc(doc(db, ORDENES, orden.id), {
      estado: 'Eliminada',
      eliminadoPorId: userId,
      eliminadoPorNombre: userName,
      eliminadoAt: serverTimestamp(),
      // 'Manual' preserva el borrado individual si después se elimina y
      // restaura la licitación completa.
      eliminadoMotivo: 'Manual',
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('licitacionesService.softDeleteOrden failed:', { id: orden?.id, error });
    throw error;
  }
};

export const setOrdenCargado = (orden, cargado, { userId, userName }) => {
  try {
    return updateDoc(doc(db, ORDENES, orden.id), {
      cargado,
      cargadoPorId: cargado ? userId : '',
      cargadoPorNombre: cargado ? userName : '',
      cargadoAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('licitacionesService.setOrdenCargado failed:', { id: orden?.id, error });
    throw error;
  }
};

/**
 * Marca/desmarca como cargadas en el mercado todas las órdenes de un tramo
 * (misma tasa o precio ofertado). Solo escribe las que cambian de estado: las
 * que ya estaban marcadas conservan quién y cuándo las cargó.
 *
 * @returns {Promise<number>} cantidad de órdenes actualizadas
 */
export const marcarCargadoGrupo = async (ordenes, cargado, { userId, userName }) => {
  try {
    const ids = (ordenes || [])
      .filter(orden => (orden.cargado === true) !== cargado)
      .map(orden => orden.id);
    for (let i = 0; i < ids.length; i += BATCH_SIZE) {
      const batch = writeBatch(db);
      ids.slice(i, i + BATCH_SIZE).forEach(id => {
        batch.update(doc(db, ORDENES, id), {
          cargado,
          cargadoPorId: cargado ? userId : '',
          cargadoPorNombre: cargado ? userName : '',
          cargadoAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      });
      // eslint-disable-next-line no-await-in-loop
      await batch.commit();
    }
    return ids.length;
  } catch (error) {
    console.error('licitacionesService.marcarCargadoGrupo failed:', error);
    throw error;
  }
};

/**
 * Marca/desmarca la caja de todas las órdenes de un grupo cliente+moneda.
 */
export const marcarCajaGrupo = (ordenes, hecha, { userId, userName }) => {
  try {
    const batch = writeBatch(db);
    ordenes.forEach(orden => {
      batch.update(doc(db, ORDENES, orden.id), {
        cajaHecha: hecha,
        cajaMarcadaPorId: hecha ? userId : '',
        cajaMarcadaPorNombre: hecha ? userName : '',
        cajaMarcadaAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    });
    return batch.commit();
  } catch (error) {
    console.error('licitacionesService.marcarCajaGrupo failed:', error);
    throw error;
  }
};

/**
 * Confirma la adjudicación de una especie en un batch atómico:
 * actualiza el array de especies del evento (corte + estado de la especie)
 * y el resultado de cada orden. Si todas las especies quedaron resueltas
 * (Adjudicada/Desierta), el evento pasa a 'Adjudicada'.
 *
 * @param {object} params
 * @param {string} params.licitacionId
 * @param {Array}  params.especies - array COMPLETO de especies ya actualizado
 * @param {Array}  params.propuestas - [{ ordenId, estadoAdjudicacion, vnAdjudicado, montoAdjudicado }]
 */
export const confirmarAdjudicacion = ({ licitacionId, especies, propuestas }) => {
  try {
    const batch = writeBatch(db);
    const todasResueltas = especies.every(e => e.estado === 'Adjudicada' || e.estado === 'Desierta');
    const licitacionUpdate = { especies, updatedAt: serverTimestamp() };
    if (todasResueltas) licitacionUpdate.estado = 'Adjudicada';
    batch.update(doc(db, LICITACIONES, licitacionId), licitacionUpdate);

    propuestas.forEach(p => {
      batch.update(doc(db, ORDENES, p.ordenId), {
        estadoAdjudicacion: p.estadoAdjudicacion,
        vnAdjudicado: toNumber(p.vnAdjudicado),
        montoAdjudicado: toNumber(p.montoAdjudicado),
        updatedAt: serverTimestamp(),
      });
    });
    return batch.commit();
  } catch (error) {
    console.error('licitacionesService.confirmarAdjudicacion failed:', { licitacionId, error });
    throw error;
  }
};
